/**
 * @module agent/adapters/services/EnrichmentTagService
 *
 * Pure parser + stripper for the agent contact-enrichment / discovery tag
 * protocol (TD-2293). Sibling of SearchWorkflowService — zero dependencies,
 * all functions pure (content in, structured payload / cleaned content out).
 *
 * Two tag families (see utils/constants/agentEnrichment.js for the protocol):
 *
 * SHIPPED (TD-2281) — marker tags with colon-separated scalar params, emitted
 * deterministically by the td-agent harness (never the model):
 *   [REVEAL_PENDING:<token>:<cost>:<remaining>]  → revealPending
 *   [REVEAL_STARTED:<jobId>]                     → revealJob
 * The token is base64url + '.' (never ':'); token and jobId are NOT UUIDs —
 * per the shipped contract they are guarded only as non-empty colon-free
 * strings (td-agent tests use ids like "job-9").
 *
 * PROPOSED (TD-2362 / TD-2292 — agent side unbuilt) — payload tags wrapping
 * JSON, UUID-guarded per the TD-2274 discipline:
 *   [AGENT_OPTIONS:<kind>:<uuid>]{json}[/AGENT_OPTIONS]      → options
 *   [DISCOVERY_RESULTS:<uuid>]{json}[/DISCOVERY_RESULTS]     → discovery
 *
 * FE → agent control replies (hidden): CONFIRM_REVEAL:<token>,
 * CANCEL_REVEAL:<token>, CHECK_REVEAL_RESULTS:<jobId>.
 */

import { isValidSearchId } from './SearchWorkflowService';
import {
  ENRICHMENT_TAG,
  ENRICHMENT_REPLY_PREFIX,
  REVEAL_MARKER
} from '../../../../utils/constants/agentEnrichment';

/** Escape a tag name for use in a RegExp (names are simple, but be safe). */
const esc = (name) => name.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);

/**
 * Match a payload tag `[NAME:<id>]<json>[/NAME]` (non-greedy body to the first
 * close tag, so JSON containing brackets is handled). Global, multi-instance.
 */
const payloadTagRe = (name) =>
  new RegExp(String.raw`\[${esc(name)}:([^\]]+)\]([\s\S]*?)\[\/${esc(name)}\]`, 'g');

/** Match the OPTIONS tag, which carries a `<kind>` before the id. */
const optionsTagRe = () =>
  new RegExp(String.raw`\[${esc(ENRICHMENT_TAG.OPTIONS)}:([\w-]+):([^\]]+)\]([\s\S]*?)\[\/${esc(ENRICHMENT_TAG.OPTIONS)}\]`, 'g');

/**
 * Match the SHIPPED reveal-pending marker `[REVEAL_PENDING:<token>:<cost>:<remaining>]`.
 * Token is base64url + '.' so it never contains ':' — three colon-split params
 * exactly (stream_mapper.py guarantees the shape). `cost`/`remaining` are
 * matched loosely (zero-or-more): the harness fail-opens to '' when a field is
 * missing and prints `None` for a null balance — both must still strip.
 */
const revealPendingRe = () =>
  new RegExp(String.raw`\[${esc(ENRICHMENT_TAG.REVEAL_PENDING)}:([^:\]]+):([^:\]]*):([^:\]]*)\]`, 'g');

/** Match the SHIPPED bulk-start marker `[REVEAL_STARTED:<jobId>]`. */
const revealStartedRe = () =>
  new RegExp(String.raw`\[${esc(ENRICHMENT_TAG.REVEAL_STARTED)}:([^:\]]+)\]`, 'g');

const safeJsonParse = (raw) => {
  try {
    return JSON.parse(String(raw).trim());
  } catch {
    return null;
  }
};

/**
 * Number when finite, else null. Tolerates the harness printing `None` (null
 * balance) or '' (fail-open missing field) — Number('') is 0, so the empty
 * string must be nulled explicitly rather than misread as a zero balance.
 */
const numberOrNull = (raw) => {
  if (raw == null || raw === '') return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
};

/**
 * Parse the first valid payload tag of `name` into `{ id, ...json }`.
 * Skips tags with a non-UUID id or unparseable JSON (degrade to prose).
 *
 * @param {string} content
 * @param {string} name - Tag name from ENRICHMENT_TAG.
 * @returns {Object|null}
 */
function parsePayloadTag(content, name) {
  for (const match of content.matchAll(payloadTagRe(name))) {
    const id = match[1];
    if (!isValidSearchId(id)) continue;
    const json = safeJsonParse(match[2]);
    if (json && typeof json === 'object') {
      return { id, ...json };
    }
  }
  return null;
}

/**
 * Parse enrichment tags from agent content into a structured payload.
 * Returns null when no valid enrichment tag is present.
 *
 * @param {string|null|undefined} content
 * @returns {{
 *   revealPending?: {token: string, cost: number|null, remaining: number|null},
 *   revealJob?: string,
 *   options?: Object,
 *   discovery?: Object
 * }|null}
 */
export function parseEnrichmentTags(content) {
  if (!content || typeof content !== 'string') return null;

  const result = {};

  // SHIPPED: [REVEAL_PENDING:<token>:<cost>:<remaining>]. The harness emits it
  // at most once per invocation, but the model could hallucinate a placeholder
  // tag (the [SEARCH_STARTED] failure mode, TD-2274) — real tokens are ALWAYS
  // base64url(payload) + '.' + base64url(mac), so prefer a dotted token and
  // fall back to the first match only when none carries a dot.
  const pendingMatches = [...content.matchAll(revealPendingRe())].filter((m) => m[1]);
  const pendingMatch =
    pendingMatches.find((m) => m[1].includes('.')) ?? pendingMatches[0] ?? null;
  if (pendingMatch) {
    result.revealPending = {
      token: pendingMatch[1],
      cost: numberOrNull(pendingMatch[2]),
      remaining: numberOrNull(pendingMatch[3])
    };
  }

  // SHIPPED: [REVEAL_STARTED:<jobId>] — jobId is NOT a UUID (e.g. "job-9").
  for (const match of content.matchAll(revealStartedRe())) {
    if (match[1]) {
      result.revealJob = match[1];
      break;
    }
  }

  // PROPOSED: options bar (carries <kind>:<uuid>).
  for (const match of content.matchAll(optionsTagRe())) {
    const kind = match[1];
    const id = match[2];
    if (!isValidSearchId(id)) continue;
    const json = safeJsonParse(match[3]);
    if (json && typeof json === 'object') {
      result.options = { id, kind, ...json };
      break;
    }
  }

  // PROPOSED: discovery results.
  const discovery = parsePayloadTag(content, ENRICHMENT_TAG.DISCOVERY_RESULTS);
  if (discovery) result.discovery = discovery;

  return Object.keys(result).length > 0 ? result : null;
}

/**
 * Remove all enrichment tags from content for display. Never returns a special
 * marker (unlike removeSearchTags) — an enrichment-only message renders its
 * card from `message.enrichment` and shows no prose.
 *
 * @param {string|null|undefined} content
 * @returns {string|null|undefined}
 */
export function removeEnrichmentTags(content) {
  if (!content || typeof content !== 'string') return content;
  let cleaned = content;
  // SHIPPED marker tags (no close tag).
  cleaned = cleaned.replace(revealPendingRe(), '');
  cleaned = cleaned.replace(revealStartedRe(), '');
  // TD-2442: bare reveal-loop poll markers (no params). [REVEAL_RESULT] is
  // PREPENDED to terminal reveal turns, so it must be stripped or it leaks into
  // the visible results/cancel text; [REVEAL_BUSY] turns strip to '' (hidden).
  cleaned = cleaned.replaceAll(REVEAL_MARKER.BUSY, '');
  cleaned = cleaned.replaceAll(REVEAL_MARKER.RESULT, '');
  // PROPOSED payload tags (open…close, JSON body).
  for (const name of [ENRICHMENT_TAG.OPTIONS, ENRICHMENT_TAG.DISCOVERY_RESULTS]) {
    cleaned = cleaned.replace(
      new RegExp(String.raw`\[${esc(name)}:[^\]]*\][\s\S]*?\[\/${esc(name)}\]`, 'g'),
      ''
    );
  }
  // Mid-stream defense: a reveal marker still streaming in (no closing ']'
  // yet) must not flash its long signed token as visible text. Only applies
  // at the very end of the accumulated content; the prompt forbids the model
  // writing these literals, so real prose never matches.
  cleaned = cleaned.replace(
    new RegExp(
      String.raw`\[(?:${esc(ENRICHMENT_TAG.REVEAL_PENDING)}|${esc(ENRICHMENT_TAG.REVEAL_STARTED)})(?::[^\]]*)?$`
    ),
    ''
  );
  return cleaned.trim();
}

// ============================================================================
// FE → AGENT CONTROL REPLIES (hidden; SHIPPED prefixes)
// ============================================================================

/** `CONFIRM_REVEAL:<token>` — user confirmed the quoted reveal. */
export function buildConfirmReply(token) {
  return `${ENRICHMENT_REPLY_PREFIX.CONFIRM_REVEAL}${token}`;
}

/** `CANCEL_REVEAL:<token>` — user declined; nothing is spent. */
export function buildCancelReply(token) {
  return `${ENRICHMENT_REPLY_PREFIX.CANCEL_REVEAL}${token}`;
}

/** `CHECK_REVEAL_RESULTS:<jobId>` — hidden bulk-reveal poll (~20s cadence). */
export function buildRevealPollMessage(jobId) {
  return `${ENRICHMENT_REPLY_PREFIX.CHECK_REVEAL}${jobId}`;
}

/** True if `content` is a hidden enrichment control reply (any prefix). */
export function isEnrichmentControlReply(content) {
  if (typeof content !== 'string') return false;
  return Object.values(ENRICHMENT_REPLY_PREFIX).some((prefix) => content.startsWith(prefix));
}

/**
 * True if `content` carries ANY reveal-namespace marker
 * (`[REVEAL_PENDING` / `[REVEAL_STARTED` / `[REVEAL_BUSY]` / `[REVEAL_RESULT]`).
 * TD-2442: the search coordinator uses this to avoid completing an active search
 * on an interleaved reveal-loop turn.
 */
export function hasRevealMarker(content) {
  return typeof content === 'string' && content.includes('[REVEAL_');
}
